import { format } from '@rental/i18n'
import { Button, Spinner } from '@rental/ui'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from 'react'
import { useLocale } from '../i18n'

/**
 * A row of a list on a pane (DESIGN.md, "A screen and its parts"): a rule
 * under it from one edge of the pane to the other, and a tint under the
 * pointer when it opens something. The pane's padding is p-4, p-6 from sm:
 * the row reaches into it. Its link (`ROW_LINK`) covers the whole row, so a
 * finger anywhere on it opens what it names; the keyboard stops on it once.
 */
export const ROW =
  'relative -mx-4 flex flex-col gap-1 border-b border-rule px-4 py-3 transition-colors last:border-b-0 has-[a]:hover:bg-surface-well sm:-mx-6 sm:px-6'

/**
 * A row that opens nothing as a whole (UI-2g): the same rule from edge to edge,
 * and no tint under the pointer, which would promise a link the row does not have.
 */
export const ROW_STILL =
  'relative -mx-4 flex flex-col gap-1 border-b border-rule px-4 py-3 last:border-b-0 sm:-mx-6 sm:px-6'

export const ROW_LINK = 'touch-target min-w-touch after:absolute after:inset-0'

/**
 * One thing in a list: what it is, its link first, with any status at the end
 * of that line; under it, quieter, what else is worth knowing.
 */
export function ListRow({
  title,
  end,
  still = false,
  children,
}: {
  title: ReactNode
  end?: ReactNode
  /** No link across the row (its title is words): no tint under the pointer. */
  still?: boolean
  children?: ReactNode
}) {
  return (
    <li className={still ? ROW_STILL : ROW}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {title}
        {end ? <div className="ms-auto flex flex-wrap items-center gap-2">{end}</div> : null}
      </div>
      {children ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted">
          {children}
        </div>
      ) : null}
    </li>
  )
}

/** What the answer on screen was asked for: a new search once its own answer is in, never sooner. */
export function useAnswered(asked: string, fetching: boolean): string {
  const [answered, setAnswered] = useState(asked)
  if (!fetching && answered !== asked) setAnswered(asked)
  return answered
}

/**
 * How many a list found, at the foot of its screen's header, where Today says
 * when its board was updated. Said politely once an answer is in, and once per
 * search, so a search that finds as many as the last is heard too; never while
 * the last answer stands in for the next, and never for another page of the
 * same search, which takes the focus instead (`ResultList`). Meanwhile a
 * spinner says the next answer is coming; nothing waits for it.
 */
export function ResultCount({
  id,
  count,
  range,
  asked,
  fetching,
  note,
  testId,
}: {
  /** Names the list, which reads it out when a page change brings the focus there. */
  id: string
  /** Empty until the first answer. */
  count: string | null
  /** Which of them this page shows, when there is more than one page. */
  range: string | null
  /** The search, its page aside. */
  asked: string
  fetching: boolean
  /** What the list holds before any search, on the same line: a line saved on the iPad. */
  note?: ReactNode
  /** The count's line, for a test that reads it (Fiscale's totals). */
  testId?: string
}) {
  const answered = useAnswered(asked, fetching)
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <p id={id} data-testid={testId} className="font-medium">
        <span role="status">
          <span key={answered}>{count}</span>
        </span>
        {range ? <span className="figures font-regular text-text-muted"> · {range}</span> : null}
      </p>
      {fetching ? <Fetching /> : null}
      {note ? <p className="text-sm text-text-muted">{note}</p> : null}
    </div>
  )
}

/**
 * The next answer on its way while the last one stays in sight: a spinner and
 * "Caricamento…" (or what the screen calls it: "Calcolo del prezzo…"), seen
 * and not said, as the answer will be (DESIGN.md, `ResultCount`). Nothing
 * waits for it.
 */
export function Fetching({ children }: { children?: ReactNode }) {
  const { messages: m } = useLocale()
  return (
    <p className="inline-flex items-center gap-2 text-sm text-text-muted">
      <Spinner className="size-4" />
      {children ?? m.app.loading}
    </p>
  )
}

/** A list's pages: the one asked for, the one on screen, and how to ask for another. */
export interface Pages {
  /** The page in the address, perhaps still on its way. */
  page: number
  /** The page whose rows are on screen. */
  shown: number
  /** Rows on the page shown. */
  rows: number
  pageSize: number
  total: number
  onPage: (page: number) => void
}

/**
 * Which page of a list is on screen, out of how many, and the ways to the
 * ones either side. The arrows keep their places from page to page; at
 * either end the one that leads nowhere stays, marked unavailable, and says
 * why through the range it is described by. Pressed twice before the next
 * page arrives, an arrow moves two pages, as Today's arrows move two days.
 */
export function Pagination({ page, shown, rows, pageSize, total, onPage }: Pages) {
  const { messages: m } = useLocale()
  const range = useId()
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  const previous = page > 1
  const next = page < lastPage
  const first = (shown - 1) * pageSize + 1
  return (
    <nav
      aria-label={m.lists.pages}
      className="-mx-4 mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-rule px-4 pt-4 sm:-mx-6 sm:px-6"
    >
      <Button
        variant="outline"
        className="rounded-sm"
        aria-disabled={previous ? undefined : true}
        aria-describedby={range}
        onClick={() => {
          // Past the end (an old link), the way back is the last page there is.
          if (previous) onPage(Math.min(page - 1, lastPage))
        }}
      >
        <ChevronLeft aria-hidden="true" className="size-4" />
        {m.bookings.previous}
      </Button>
      <p id={range} className="figures">
        {rows > 0 ? format(m.bookings.range, { first, last: first + rows - 1, total }) : null}
      </p>
      <Button
        variant="outline"
        className="rounded-sm"
        aria-disabled={next ? undefined : true}
        aria-describedby={range}
        onClick={() => {
          if (next) onPage(page + 1)
        }}
      >
        {m.bookings.next}
        <ChevronRight aria-hidden="true" className="size-4" />
      </Button>
    </nav>
  )
}

/**
 * After another page is asked for and has arrived, the list's top comes into
 * sight, its count above it, and the focus goes to the list: it is read out
 * by its count and range, and the keyboard goes on from its first row
 * rather than from the foot of the page before.
 */
function usePageFocus(
  list: RefObject<HTMLElement | null>,
  countId: string,
  shown: number | undefined,
  settled: boolean,
) {
  const wanted = useRef<number | null>(null)
  useEffect(() => {
    if (!settled || wanted.current === null) return
    const page = wanted.current
    wanted.current = null
    if (page !== shown) return
    document.getElementById(countId)?.scrollIntoView({ block: 'start' })
    list.current?.focus({ preventScroll: true })
  })
  return (page: number) => {
    wanted.current = page
  }
}

/**
 * A list's rows on a pane of their own, named by its count, and its pages
 * under them. While the next answer is on its way the last one stays, marked
 * busy, and nothing is disabled.
 */
export function ResultList({
  countId,
  fetching,
  pages,
  children,
}: {
  countId: string
  fetching: boolean
  pages?: Pages | undefined
  children: ReactNode
}) {
  const list = useRef<HTMLUListElement>(null)
  const turnTo = usePageFocus(list, countId, pages?.shown, !fetching)
  return (
    <div className="glass-pane flex flex-col rounded-card p-4 sm:p-6">
      <ul
        ref={list}
        tabIndex={-1}
        aria-labelledby={countId}
        aria-busy={fetching}
        className="flex flex-col focus:outline-none"
      >
        {children}
      </ul>
      {pages ? (
        <Pagination
          {...pages}
          onPage={(page) => {
            turnTo(page)
            pages.onPage(page)
          }}
        />
      ) : null}
    </div>
  )
}
